import { Divider } from '@deliveryhero/pd-cookbook/components/Divider';
import { Dropdown } from '@deliveryhero/pd-cookbook/components/Dropdown';
import { Grid } from '@deliveryhero/pd-cookbook/components/Grid';
import { Message } from '@deliveryhero/pd-cookbook/components/Message';
import { TextInput } from '@deliveryhero/pd-cookbook/components/TextInput';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';

import { Card } from 'components/Card';
import FormContainer from 'components/Forms/FormContainer';
import { LoadingAnimation } from 'components/LoadingAnimation';
import {
  EmployeeRangeOptions,
  Industry,
  Reason
} from 'components/UpgradePlan/types';
import { phoneCountryCodes } from 'share/phone-country-codes';

import { FormLabelText } from './constants';
import { UpgradePlanFormProps } from './types';
import { getUpgradePlanFormSchema } from './validation';

import './styles.scss';

export const UpgradePlanForm = (props: UpgradePlanFormProps) => {
  const {
    cities,
    detailFormRef,
    initialValues,
    isLoading,
    onSubmit,
    user,
    defaultPhoneCountryCode,
    dropdownOptions
  } = props;

  if (isLoading) return <LoadingAnimation />;

  return (
    <FormContainer
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={getUpgradePlanFormSchema()}
      formRef={detailFormRef}
    >
      {({ handleChange, values, touched, errors, setFieldValue }) => (
        <Card className="upgrade-plan-form-container">
          <Typography type="title-md" color="neutral-primary">
            {FormLabelText.TITLE_ORGANISATION}
          </Typography>
          <Grid rowGap="24px">
            <TextInput
              id="company"
              name="company"
              label={FormLabelText.COMPANY_LABEL}
              value={user.company.name}
              disabled={true}
            />
            <Dropdown
              id="industry"
              onChange={(e) => setFieldValue('industry', e.value)}
              options={dropdownOptions.industries.map((industry) => ({
                label: Industry[industry],
                value: industry,
                selected: industry === values.industry
              }))}
              zIndex={10}
            />
            <Dropdown
              id="no_of_employees"
              onChange={(e) => setFieldValue('no_of_employees', e.value)}
              options={EmployeeRangeOptions.map((option) => ({
                ...option,
                value: option.value.toString(),
                selected: option.value == values.no_of_employees
              }))}
              zIndex={10}
            />
            <TextInput
              id="street"
              name="street"
              label={FormLabelText.STREET_LABEL}
              value={values.street}
              onChange={handleChange}
              invalid={touched.street && Boolean(errors.street)}
              isError={touched.street && Boolean(errors.street)}
              errorMessage={(touched.street && errors.street) || ''}
            />
            <Grid columnGap="8px" gridTemplateColumns="1fr 1fr">
              <TextInput
                id="zip"
                name="zip"
                label={FormLabelText.ZIP_LABEL}
                value={values.zip}
                onChange={handleChange}
                invalid={touched.zip && Boolean(errors.zip)}
                isError={touched.zip && Boolean(errors.zip)}
                errorMessage={(touched.zip && errors.zip) || ''}
              />
              <Dropdown
                id="city"
                onChange={(e) => setFieldValue('city', e.label)}
                options={cities.map((city) => ({
                  label: city,
                  selected: city === values.city
                }))}
                zIndex={10}
              />
            </Grid>
          </Grid>
          <div className="upgrade-plan-form-container__divider">
            <Divider />
          </div>
          <Typography type="title-md" color="neutral-primary">
            {FormLabelText.TITLE_DETAILS}
          </Typography>
          <Grid rowGap="24px">
            <Grid columnGap="8px" gridTemplateColumns="auto auto">
              <TextInput
                id="first_name"
                name="first_name"
                label={FormLabelText.FIRST_NAME_LABEL}
                value={user.first_name}
                onChange={handleChange}
                disabled={true}
              />
              <TextInput
                id="last_name"
                name="last_name"
                label={FormLabelText.LAST_NAME_LABEL}
                value={user.last_name}
                onChange={handleChange}
                disabled={true}
              />
            </Grid>
            <TextInput
              id="email"
              name="email"
              label={FormLabelText.EMAIL_LABEL}
              value={user.email}
              onChange={handleChange}
              disabled={true}
            />
            <Grid columnGap="8px" gridTemplateColumns="auto 1fr">
              <TextInput
                id="mobile_country_code"
                name="mobile_country_code"
                label={FormLabelText.MOBILE_CODE_LABEL}
                value={
                  phoneCountryCodes.find(
                    (code) => code.value === user.mobile_country_code
                  )?.label || defaultPhoneCountryCode
                }
                disabled={true}
              />
              <TextInput
                id="mobile_number"
                name="mobile_number"
                label={FormLabelText.MOBILE_LABEL}
                value={user.mobile_number}
                disabled={true}
              />
            </Grid>
            <Dropdown
              id="reason"
              onChange={(e) => setFieldValue('reason', e.value)}
              options={dropdownOptions.reasons.map((reason) => ({
                label: Reason[reason],
                value: reason,
                selected: reason === values.reason
              }))}
              zIndex={10}
            />
            {(errors.city ||
              errors.industry ||
              errors.no_of_employees ||
              errors.reason) && (
              <Message
                id="dropdown_error_message"
                message={'Select a '.concat(
                  [
                    errors.city || '',
                    errors.industry || '',
                    errors.no_of_employees || '',
                    errors.reason || ''
                  ]
                    .filter((n) => n)
                    .map((error) => ` ${error}`)
                    .join()
                )}
                kind={'error'}
              />
            )}
          </Grid>
        </Card>
      )}
    </FormContainer>
  );
};
